import { ApiService } from '~/services/ApiService';
import { SUPPORT_URLS } from '~/constants/urls';

/**
 * What a report is about: exactly one of the ids, or none for a billing request.
 */
export interface IReportTarget {
  MemberId?: number;
  PostId?: number;
  CommentId?: number;
  PhotoId?: number;
  /** A conversation the member is part of */
  MessageId?: number;
}

export type SupportRequestType = 'Report' | 'Billing';

/**
 * Member-chosen report reasons, aligned with the Community Conduct Rules. Values are stored server-side: never renumber.
 */
export enum ReportReason {
  Scam = 1,
  Spam = 2,
  FalsePortrayal = 3,
  Harassment = 4,
  Intolerance = 5,
  Trolling = 6,
  Disparagement = 7,
  DonationRequest = 8,
  PromotionalPosting = 9,
  PhotoPolicy = 10,
  UncreditedArtwork = 11,
  Underage = 12,
  IllegalActivity = 13,
  Other = 99,
}

export type ReportSubject = 'member' | 'post' | 'comment' | 'photo' | 'conversation';

export interface ISupportTicket {
  Reference: string;
  TicketUrl: string;
}

/**
 * Service for handling support ticket submissions
 */
export class SupportService {
  /**
   * Constructs URL for a general support ticket form
   * @param title The title for the support ticket
   */
  static getTicketUrl(title: string): string {
    return SUPPORT_URLS.atlassian.support.helpCenter;
  }

  /**
   * Records a support request on the server, which builds the staff context from its own data, and returns the
   * prefilled support-portal link to open
   * @param requestType Report (exactly one target) or Billing (no target)
   * @param target The reported member, post, comment or photo
   * @param appVersion App version information
   * @param device Device information
   * @param reason Reason code chosen by the member, reports only
   */
  static async createSupportRequest(
    requestType: SupportRequestType,
    target: IReportTarget,
    appVersion: string,
    device: string,
    reason?: number
  ): Promise<ISupportTicket> {
    return ApiService.post<ISupportTicket>('support', {
      ...target,
      RequestType: requestType,
      Reason: reason,
      AppVersion: appVersion,
      Device: device,
    });
  }
}
